import { AGENT_RECORDING_CHANNEL, CANDIDATE_RECORDING_CHANNEL, connectSilentKeepAliveToMerger, connectTrackToRecordingMixer } from './browser-audio.util'
import {
    createRecordingVideoCompositor,
    type RecordingVideoCompositor,
    type RecordingVideoCompositorOptions,
    type RecordingVideoSources,
} from './compose-recording-video.util'
import type { InterviewStreamState } from './interview-stream-host.type'

export type RecordingStreamResources = {
    audioContext: AudioContext
    destination: MediaStreamAudioDestinationNode
    merger: ChannelMergerNode
    /** Mute gate on the local-mic path — every local track clone routes through it. */
    localMicGate: GainNode
    composedStream: MediaStream
    mixTracks: MediaStreamTrack[]
    /** Null only when the canvas compositor could not be created (raw-track fallback). */
    compositor: RecordingVideoCompositor | null
}

const RECODING_SAMPLE_RATE = 48000

function mixTrackIntoMerger(
    audioContext: AudioContext,
    merger: ChannelMergerNode,
    track: MediaStreamTrack | null,
    mixTracks: MediaStreamTrack[],
    connectedTrackIds: Set<string>,
    gate?: GainNode | null
): boolean {
    if (!track || connectedTrackIds.has(track.id)) {
        return false
    }
    // Only the local mic is gated; every ungated track is the AI interviewer's voice.
    const cloned = connectTrackToRecordingMixer(audioContext, merger, track, gate, AGENT_RECORDING_CHANNEL)
    if (cloned) {
        mixTracks.push(cloned)
        connectedTrackIds.add(track.id)
        return true
    }
    return false
}

export function getRecordingVideoSources(streams: InterviewStreamState): RecordingVideoSources {
    return { cameraTrack: streams.videoTrack, screenTrack: streams.screenTrack }
}

export async function buildRecordingStreamFromState(
    streams: InterviewStreamState,
    connectedTrackIds = new Set<string>(),
    /** Frame size and rate of the recording (INT-1059); the compositor's 1280×720 @ 30 fps when omitted. */
    videoOptions?: Pick<RecordingVideoCompositorOptions, 'width' | 'height' | 'fps'>
): Promise<RecordingStreamResources | null> {
    const audioTrack = streams.audioTrack
    const remoteAudioTrack = streams.remoteAudioTrack
    const fallbackVideoTrack = streams.activeVideoTrack ?? streams.videoTrack
    if (!audioTrack && !remoteAudioTrack && !fallbackVideoTrack) {
        return null
    }

    const audioContext = new AudioContext({ sampleRate: RECODING_SAMPLE_RATE })
    if (audioContext.state === 'suspended') {
        await audioContext.resume()
    }

    const destination = audioContext.createMediaStreamDestination()
    // Stereo destination so Chrome MediaRecorder sees a true 2-channel stream.
    // Candidate mic on the left channel, AI interviewer on the right (INT-983): talent-ai re-transcribes the candidate
    // channel after the call, and the HLS conversion mixes both into both ears for playback.
    destination.channelCount = 2
    destination.channelCountMode = 'explicit'
    destination.channelInterpretation = 'discrete'

    // ChannelMergerNode locks channelCount=1 / channelCountMode=explicit — do not set them
    // (throws DOMException). Stereo comes from 2 merger inputs into a 2-channel destination.
    const merger = audioContext.createChannelMerger(2)
    merger.connect(destination)

    const mixTracks: MediaStreamTrack[] = []
    const occupiedChannels = new Set<0 | 1>()

    // Local mic runs through a mute gate so the recorder honors mic mute even
    // though the mixed tracks are clones (track.enabled doesn't propagate).
    const localMicGate = audioContext.createGain()
    localMicGate.connect(merger, 0, CANDIDATE_RECORDING_CHANNEL)

    // Silence keep-alive fills whichever channel has no real track yet.
    if (mixTrackIntoMerger(audioContext, merger, audioTrack, mixTracks, connectedTrackIds, localMicGate)) {
        occupiedChannels.add(CANDIDATE_RECORDING_CHANNEL)
    }
    if (mixTrackIntoMerger(audioContext, merger, remoteAudioTrack, mixTracks, connectedTrackIds)) {
        occupiedChannels.add(AGENT_RECORDING_CHANNEL)
    }

    // Keep unused merger inputs alive with silence so the destination is 2-channel
    // before MediaRecorder.start() (bot audio is often hot-plugged later).
    connectSilentKeepAliveToMerger(audioContext, merger, occupiedChannels)

    const mixedTrack = destination.stream.getAudioTracks()[0]
    if (mixedTrack) {
        void mixedTrack.applyConstraints({ channelCount: 2 }).catch(() => undefined)
    }

    const composed = new MediaStream()
    let compositor: RecordingVideoCompositor | null = null

    // INTENTIONAL: video always goes through the compositor, even with the camera off,
    // so the recorded stream's track set never changes. Browsers stop MediaRecorder for
    // good when a track is added to or removed from its stream (InvalidModificationError).
    try {
        compositor = createRecordingVideoCompositor({ ...getRecordingVideoSources(streams), ...videoOptions })
        composed.addTrack(compositor.getVideoTrack())
    } catch {
        compositor = null
        if (fallbackVideoTrack) {
            composed.addTrack(fallbackVideoTrack)
        }
    }

    if (mixedTrack) {
        composed.addTrack(mixedTrack)
    }

    if (composed.getVideoTracks().length === 0 && composed.getAudioTracks().length === 0) {
        compositor?.stop()
        mixTracks.forEach((track) => track.stop())
        await audioContext.close().catch(() => undefined)
        return null
    }

    return { audioContext, destination, merger, localMicGate, composedStream: composed, mixTracks, compositor }
}

/** Connect a late-arriving track into an active recording mixer (mic via its gate on the left, AI voice on the right). */
export function connectTrackToActiveMixer(
    audioContext: AudioContext,
    merger: ChannelMergerNode,
    track: MediaStreamTrack | null,
    mixTracks: MediaStreamTrack[],
    connectedTrackIds: Set<string>,
    gate?: GainNode | null
): void {
    mixTrackIntoMerger(audioContext, merger, track, mixTracks, connectedTrackIds, gate)
}

/**
 * Retarget the recording compositor when the camera turns off/on, switches device,
 * or screen share starts/stops. Never touches the recorded stream's tracks.
 * Returns whether the drawn sources changed.
 */
export function updateRecordingVideoComposition(compositor: RecordingVideoCompositor | null, streams: InterviewStreamState): boolean {
    return compositor?.setSources(getRecordingVideoSources(streams)) ?? false
}
